import { useState } from 'react';

const BENEFITS_IMG = 'https://static.readdy.ai/image/d21c811a591cac7dc9306c593ec79059/5453749a186009a4ba5216e64cc9f94c.png';

interface HeroSectionProps {
  onFormSubmitSuccess: () => void;
}

export default function HeroSection({ onFormSubmitSuccess }: HeroSectionProps) {
  const [formData, setFormData] = useState({
    services: [] as string[],
    name: '',
    phone: '',
    address: '',
    date: '',
    time: '08:00 AM - 10:00 AM',
    textUpdates: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleServiceChange = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || submitted) return;
    setSubmitting(true);
    setError('');

    const body = new URLSearchParams();
    body.append('services', formData.services.join(', '));
    body.append('name', formData.name);
    body.append('phone', formData.phone);
    body.append('address', formData.address);
    body.append('date', formData.date);
    body.append('time', formData.time);
    body.append('textUpdates', formData.textUpdates ? 'Yes' : 'No');

    try {
      const res = await fetch('https://readdy.ai/api/form/d6fkae0r225iqdhs0bmg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        onFormSubmitSuccess();
      } else {
        setError('Submission failed. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="top" className="bg-white pt-4 pb-6 lg:pb-10">
      <div className="max-w-6xl mx-auto px-4">
        {/* Top Banner - Headline & Call Button */}
        <div className="text-center mb-6 space-y-3">
          <a
            href="tel:3214468130"
            className="inline-flex items-center justify-center gap-2 bg-[#2E7DD1] hover:bg-[#3B8FE8] text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-fill"></i>
            Call Now - (321) 446-8130
          </a>
          <p className="text-sm font-semibold text-[#2E7DD1] tracking-wide uppercase">
            This Is a Limited-Time Offer
          </p>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-[#2E7DD1] uppercase tracking-tight">
            Air Duct &amp; Dryer Vent Cleaning
          </h1>
          <p className="text-2xl lg:text-3xl font-extrabold text-[#F7B731] uppercase">
            Starting at Just $79. Book Today!
          </p>
          <p className="text-sm text-gray-600 leading-relaxed max-w-md mx-auto">
            We only take payment once our job is complete. Payment is completed the day of the appointment.
          </p>
        </div>

        {/* Booking Form - Full Width on Mobile, Centered on Desktop */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left Column - Booking Form or Confirmation */}
          <div id="booking-form" className="bg-gradient-to-b from-[#2E7DD1] to-[#3B8FE8] rounded-2xl p-6 lg:p-8 shadow-2xl">
            {submitted ? (
              <div className="flex items-center justify-center w-full aspect-square bg-[#2E7DD1] rounded-xl">
                <p className="text-white text-xl font-bold text-center px-6 leading-relaxed">
                  Thank you for your submission. We will see you soon!
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-5">
                  <h2 className="text-2xl lg:text-3xl font-extrabold text-white uppercase tracking-wide">
                    Schedule <span className="text-[#F7B731]">an <em>Appointment</em></span>
                  </h2>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-readdy-form
                  id="appointment-form"
                >
                  {/* Service Checkboxes */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-200 mb-2">
                      Please select the service required
                    </label>
                    <div className="space-y-2">
                      {[
                        { label: 'Air Duct Cleaning $99', value: 'Air Duct Cleaning $99' },
                        { label: 'Dryer Vent Cleaning $79', value: 'Dryer Vent Cleaning $79' },
                        { label: 'Both Services $139 (Save $39!)', value: 'Both Services $139' },
                        { label: 'Free Full House Inspection', value: 'Free Full House Inspection' },
                      ].map((s) => (
                        <label
                          key={s.value}
                          className="flex items-center gap-2 text-sm text-white cursor-pointer hover:text-[#F7B731] transition-colors"
                        >
                          <input
                            type="checkbox"
                            name="services"
                            value={s.value}
                            checked={formData.services.includes(s.value)}
                            onChange={() => handleServiceChange(s.value)}
                            className="w-4 h-4 rounded border-gray-400 text-[#F7B731] focus:ring-[#F7B731]"
                          />
                          {s.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Name & Phone */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-200 mb-1">
                        Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Name"
                        required
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B731] bg-white text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-200 mb-1">
                        Phone <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="Phone"
                        required
                        pattern="[0-9()#&+*\-=. ]+"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B731] bg-white text-gray-900"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-200 mb-1">
                      Please provide your address <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Street Address"
                      required
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B731] bg-white text-gray-900"
                    />
                  </div>

                  {/* Date & Time */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-200 mb-1">
                        Date of Service <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        required
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B731] bg-white text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-200 mb-1">
                        Time of Service <span className="text-red-400">*</span>
                      </label>
                      <select
                        name="time"
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        required
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B731] bg-white text-gray-900"
                      >
                        <option value="08:00 AM - 10:00 AM">08:00 AM - 10:00 AM</option>
                        <option value="09:00 AM - 11:00 AM">09:00 AM - 11:00 AM</option>
                        <option value="10:00 AM - 12:00 PM">10:00 AM - 12:00 PM</option>
                        <option value="12:00 PM - 02:00 PM">12:00 PM - 02:00 PM</option>
                        <option value="02:00 PM - 04:00 PM">02:00 PM - 04:00 PM</option>
                        <option value="04:00 PM - 06:00 PM">04:00 PM - 06:00 PM</option>
                      </select>
                    </div>
                  </div>

                  {/* Text Updates Consent */}
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="textUpdates"
                      checked={formData.textUpdates}
                      onChange={(e) => setFormData({ ...formData, textUpdates: e.target.checked })}
                      required
                      className="w-4 h-4 rounded border-gray-400 text-[#F7B731] focus:ring-[#F7B731] mt-0.5"
                    />
                    <span className="text-xs text-gray-300">
                      I would like to receive updates by text
                    </span>
                  </label>

                  {/* Inline Error */}
                  {error && (
                    <div className="bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2">
                      <i className="ri-error-warning-fill text-base"></i>
                      {error}
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#F7B731] hover:bg-[#F5A623] text-white font-extrabold py-3.5 px-6 rounded-lg transition-colors whitespace-nowrap text-lg shadow-lg cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Right Column - Supporting Content */}
          <div className="space-y-5">
            {/* Benefits Image */}
            <div className="w-full h-64 lg:h-72 rounded-xl overflow-hidden">
              <img
                src={BENEFITS_IMG}
                alt="Air duct cleaning before and after results showing dramatic improvement"
                title="Sunshine Air Pros air duct cleaning before after"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Bullet Points */}
            <div className="space-y-2 pt-2">
              <p className="text-center text-lg font-bold text-[#2E7DD1] uppercase">
                Schedule Your Appointment Now!
              </p>
              <ul className="space-y-2">
                {[
                  'No hidden fees',
                  'Qualified & well-trained technicians',
                  'Fully insured with warranty',
                  'Next-day service available',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-checkbox-circle-fill text-[#42A5F5] text-lg"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Trust Badges Row */}
            <div className="flex items-center justify-center gap-6 pt-2">
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-[#F7B731]/15 flex items-center justify-center">
                  <i className="ri-shield-check-fill text-[#F7B731] text-2xl"></i>
                </div>
                <span className="text-xs font-semibold text-[#2E7DD1] mt-1">Insured</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-[#42A5F5]/15 flex items-center justify-center">
                  <i className="ri-time-fill text-[#42A5F5] text-2xl"></i>
                </div>
                <span className="text-xs font-semibold text-[#2E7DD1] mt-1">Next-Day</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-[#F7B731]/15 flex items-center justify-center">
                  <i className="ri-award-fill text-[#F7B731] text-2xl"></i>
                </div>
                <span className="text-xs font-semibold text-[#2E7DD1] mt-1">Warranty</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-[#42A5F5]/15 flex items-center justify-center">
                  <i className="ri-money-dollar-circle-fill text-[#42A5F5] text-2xl"></i>
                </div>
                <span className="text-xs font-semibold text-[#2E7DD1] mt-1">Guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
